import styled, {css} from "styled-components";
import {brand, fontFamilies} from "theme/tokens";
import {pulse} from "./InstantLiveCard.styles";
import {RingBox} from "./RecipientsRing.styles";
import {bottomFade} from "./SuccessCard.styles";

/** The live card's recipient list (kitecorp/transferify-ui#3320). Every colour reads `theme.tokens`. */

/** How much of the third row shows under the fade, saying there is more to scroll to. */
export const LIST_PEEK_PX = 28;

export const Eyebrow = styled.h3`
	margin: 0;
	align-self: start;
	display: flex;
	align-items: center;
	min-height: 30px;
	font: 600 11.5px ${fontFamilies.sans};
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: ${({theme}) => theme.tokens.ink3};
`;

/**
 * The eyebrow and the ring share the first row (kitecorp/transferify-ui#3341): the label sits at the top left of
 * the ring's square, where the circle has curved well away from it, rather than on a 30px line of its own above the
 * ring. That line is what the card with two recipients could not spare at 1366x768. With nobody yet there is no
 * ring, and the waiting line takes the next row under the eyebrow as before.
 */
export const Section = styled.section`
	display: grid;
	min-width: 0;

	& > ${Eyebrow}, & > ${RingBox} {
		grid-area: 1 / 1;
	}
`;

/**
 * Two rows show, and the rest scroll inside the list under the success card's own bottom fade, so a crowd of
 * recipients never pushes "Send another" out of the card. The height is measured (`useTwoRowHeight`), because
 * a row's figures can wrap in a long locale.
 */
export const List = styled.ul<{$scrolls: boolean}>`
	list-style: none;
	margin: 0;
	padding: 0;
	min-width: 0;
	${({$scrolls}) => $scrolls && css`
		overflow-x: hidden;
		overflow-y: auto;
		overscroll-behavior: contain;
		scrollbar-width: thin;
		${bottomFade(LIST_PEEK_PX)};

		&:focus-visible {
			outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
			outline-offset: 2px;
		}
	`}
`;

/**
 * 8px above and below a row's text, and 4px under the ring: down from 11 and 6 so the card with two recipients fits
 * a 1366x768 window (kitecorp/transferify-ui#3341).
 */
const rowLine = css`
	display: grid;
	column-gap: 10px;
	align-items: center;
	padding: 8px 0;
	border-top: 1px solid ${({theme}) => theme.tokens.line};

	&:first-child {
		border-top: 0;
		padding-top: 4px;
	}
`;

export const Row = styled.li`
	${rowLine};
	grid-template-columns: 14px minmax(0, 1fr);
	row-gap: 2px;

	& > :not(:first-child) {
		grid-column: 2;
	}
`;

/** The row's state: a green dot downloading, a hollow one for a recipient that only opened the link. */
export const Mark = styled.span<{$downloading: boolean}>`
	grid-row: 1;
	justify-self: center;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	${({theme, $downloading}) => $downloading
		? css`background: ${brand.live};`
		: css`box-shadow: inset 0 0 0 2px ${theme.tokens.ink3};`}
`;

/** A finished recipient's tick, and the fold line's. */
export const Tick = styled.svg`
	grid-row: 1;
	justify-self: center;
	width: 14px;
	height: 14px;
	color: ${({theme}) => theme.tokens.teal};
`;

export const Name = styled.span`
	min-width: 0;
	font: 600 14px/1.35 ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

/** The row's figures; a middle dot is drawn between its parts, so the words never carry one. */
export const Figures = styled.span`
	font-size: 12.5px;
	line-height: 1.35;
	color: ${({theme}) => theme.tokens.ink2};
	font-variant-numeric: tabular-nums;

	& > * + *::before {
		content: "·";
		margin-inline: 0.35em;
		font-weight: 400;
		color: ${({theme}) => theme.tokens.ink2};
	}

	strong {
		font-weight: 700;
		color: ${({theme}) => theme.tokens.ink};
	}
`;

export const Done = styled.span`
	font-weight: 600;
	color: ${({theme}) => theme.tokens.teal};
`;

export const FoldRow = styled.li`
	${rowLine};
	grid-template-columns: 14px minmax(0, 1fr) auto;
`;

export const FoldLabel = styled.span`
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const FoldToggle = styled.button`
	position: relative;
	border: 0;
	background: transparent;
	padding: 4px 0;
	color: ${({theme}) => theme.tokens.linkInk};
	font: 600 13px ${fontFamilies.sans};
	cursor: pointer;

	&::before {
		content: "";
		position: absolute;
		inset-inline: -6px;
		top: 50%;
		height: 44px;
		transform: translateY(-50%);
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Nobody yet: the waiting line with its amber dot, and who will appear here. */
export const Empty = styled.div`
	display: grid;
	grid-template-columns: 14px minmax(0, 1fr);
	column-gap: 10px;
	padding: 8px 0 4px;
`;

export const WaitDot = styled.span`
	justify-self: center;
	margin-top: 6px;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	${({theme}) => css`
		background: ${theme.tokens.orange};
		animation: ${pulse(theme.tokens.orange)} 1.6s infinite;
	`}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

export const EmptyTitle = styled.p`
	margin: 0;
	font: 600 14px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
`;

export const EmptyHint = styled.p`
	margin: 0;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;
